import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import { errorText } from '../../utils/errorText';
import { can } from '../../utils/permissions';
import { byUrgency, pipelineLink } from '../../utils/pipelineTasks';
import PipelineStatus from './PipelineStatus';
import ShareMenu from '../share/ShareMenu';
import { appLink } from '../../utils/share';
import * as S from '../specimens/styles';

/*
 * "My tasks" on Home: the culture-pipeline tasks given to me
 * (GET /pipeline/tasks?mine=1), started first, then ready, then waiting;
 * each opens its isolate on the Pipeline tab, and a ready one can be started
 * from here (the next stage's owner is emailed). Someone who hands tasks out
 * (pipeline:assign) also sees how many open tasks nobody has, and can list
 * them. Nothing at all for someone who works no lab tasks, nor for someone
 * with none -- Home stays the isolates hub.
 *
 * Share (2026-10-19): the list as a text summary, or a Markdown or CSV file
 * -- each task with its isolate, lab code, stage, status and link.
 */
const NL = '\n';

/** One line per task, its link indented under it. */
export function tasksText(tasks, t) {
  return byUrgency(tasks).map((task) => [task.specimen_code, task.lab_code, t(`stage.${task.stage}`),
    t(`status.${task.status}`, { defaultValue: task.status })].filter(Boolean).join(' · ')
    + `${NL}  ${appLink(pipelineLink(task.specimen_id))}`).join(NL);
}

export function tasksMarkdown(tasks, t) {
  const lines = tasksText(tasks, t).split(NL).map((l) => (l.startsWith('  ') ? l : `- ${l}`));
  return `# ${t('mine.title')}${NL}${NL}${lines.join(NL)}${NL}`;
}

export function tasksCsv(tasks, t) {
  const cell = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
  const rows = byUrgency(tasks).map((task) => [task.specimen_code, task.lab_code, t(`stage.${task.stage}`),
    task.status, appLink(pipelineLink(task.specimen_id))].map(cell).join(','));
  return ['isolate,lab_code,stage,status,link', ...rows].join(NL) + NL;
}
export default function MyTasksPanel({ user }) {
  const { t } = useTranslation(['pipeline', 'common']);
  const navigate = useNavigate();
  const works = can(user, 'pipeline:work');
  const assigns = can(user, 'pipeline:assign');
  const [mine, setMine] = useState(null);
  const [free, setFree] = useState(null);
  const [showFree, setShowFree] = useState(false);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(null);

  const load = useCallback(() => {
    if (!works) return Promise.resolve();
    const asks = [api.get('/pipeline/tasks', { params: { mine: 1 } }).then((r) => setMine(r.data.items || []))];
    if (assigns) {
      asks.push(api.get('/pipeline/tasks', { params: { unassigned: 1, status: 'ready,in_progress' } })
        .then((r) => setFree(r.data.items || [])));
    }
    return Promise.all(asks).catch(() => setError({ key: 'loadFailed' }));
  }, [works, assigns]);

  useEffect(() => { load(); }, [load]);

  if (!works || mine === null) return null;
  if (mine.length === 0 && !(free && free.length)) return null;

  const start = (task) => {
    setBusy(task.task_id);
    api.post(`/pipeline/tasks/${task.task_id}/start`)
      .then(load)
      .catch((e) => setError({ key: 'actionFailed', error: errorText(e) }))
      .finally(() => setBusy(null));
  };

  const row = (task) => (
    <li key={task.task_id} className="hub-record" data-testid="my-task" data-status={task.status}
      style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
      <bdi dir="ltr" style={S.mono}>{task.specimen_code}</bdi>
      {task.lab_code && <span style={S.hint}>{task.lab_code}</span>}
      <span>{t(`stage.${task.stage}`)}</span>
      <PipelineStatus status={task.status} />
      <span style={{ marginInlineStart: 'auto', display: 'flex', gap: 6 }}>
        {task.status === 'ready' && task.assignee_id === user?.user_id && (
          <button type="button" style={smallBtn} disabled={busy === task.task_id} title={t('tip.start')}
            data-testid="my-task-start" onClick={() => start(task)}>{t('action.start')}</button>
        )}
        <button type="button" style={smallBtn} title={t('tip.open')} data-testid="my-task-open"
          onClick={() => navigate(pipelineLink(task.specimen_id))}>{t('action.open')}</button>
      </span>
    </li>
  );

  return (
    <section style={{ ...S.card, marginBottom: 16 }} aria-labelledby="my-tasks-title" data-testid="my-tasks">
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        <h2 id="my-tasks-title" style={S.sectionTitle}>{t('mine.title')} ({mine.length})</h2>
        {mine.length > 0 && (
          <ShareMenu testid="my-tasks-share" title={t('mine.title')}
            text={`${t('mine.title')}${NL}${NL}${tasksText(mine, t)}${NL}`}
            exports={[
              { key: 'md', label: t('common:share.downloadMarkdown'), filename: 'my-tasks.md', mime: 'text/markdown',
                content: () => tasksMarkdown(mine, t) },
              { key: 'csv', label: t('mine.downloadCsv'), filename: 'my-tasks.csv', mime: 'text/csv',
                content: () => tasksCsv(mine, t) },
            ]} />
        )}
      </div>
      {error && <p role="alert" style={S.error}>{t(error.key, { error: error.error })}</p>}
      {mine.length === 0 ? <p style={S.hint}>{t('mine.empty')}</p>
        : <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'grid', gap: 6 }}>{byUrgency(mine).map(row)}</ul>}
      {assigns && free && (
        <div style={{ marginTop: 12 }}>
          <button type="button" style={S.ghostBtn} title={t('tip.showUnassigned')} aria-expanded={showFree}
            data-testid="my-tasks-unassigned" onClick={() => setShowFree(!showFree)}>
            {t('mine.unassigned')} ({free.length}) · {showFree ? t('mine.hide') : t('mine.show')}
          </button>
          {showFree && (free.length === 0 ? <p style={S.hint}>{t('mine.noUnassigned')}</p> : (
            <ul style={{ listStyle: 'none', padding: 0, margin: '8px 0 0', display: 'grid', gap: 6 }}>
              {byUrgency(free).map(row)}
            </ul>
          ))}
        </div>
      )}
    </section>
  );
}

const smallBtn = { ...S.ghostBtn, padding: '4px 10px', fontSize: 13 };
